import React from 'react';
import styled from 'styled-components';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { mainClass, SelectClasses } from '../../utils/animatedStyles';
import { RGBA_COLORS } from '../../utils/colors';
import { delay } from '../../utils/helpers';
import { ArrowIcon } from '../svgs/IconArrow';

export interface SelectItemType {
  title: string;
  value: string;
  id: number | string;
}

interface SelectProps {
  data: SelectItemType[];
  selected: SelectItemType | null;
  placeHolder?: string;
  icon?: React.FC;
  label?: string;
  onChange: (item: SelectItemType) => void;
}

export const Select: React.FC<SelectProps> = ({
  data,
  selected,
  onChange,
  label,
  placeHolder,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const modalRef = React.useRef<HTMLUListElement>(null);
  const hideList = async () => {
    if (modalRef && modalRef.current) {
      modalRef.current.classList.remove(SelectClasses.in);
      modalRef.current.classList.add(SelectClasses.out);
      await delay(100);
      setIsOpen(false);
    }
  };

  const { ref } = useOutsideClick(() => {
    if (isOpen) {
      hideList();
    }
  });
  return (
    <WrapperS className="ov-select" ref={ref}>
      {label ? <LabelS>{label}</LabelS> : null}

      <SelectBtnS isOpen={isOpen} onClick={() => setIsOpen(true)}>
        {Icon ? (
          <span className="svgIconSelect">
            <Icon />
          </span>
        ) : null}
        <SelectSpanS active={!!selected}>{selected ? selected.title : placeHolder}</SelectSpanS>
        {data[1] ? <ArrowIcon /> : null}
      </SelectBtnS>

      {!isOpen ? null : (
        <ItemsWrapperS ref={modalRef} className={`${mainClass} ${SelectClasses.in}`}>
          {data && data[0] ? (
            data.map((itm) => {
              if (selected && selected.id === itm.id) return null;
              return (
                <ItemS key={itm.id}>
                  <span
                    onClick={() => {
                      hideList();
                      onChange(itm);
                    }}
                  >
                    {itm.title}
                  </span>
                </ItemS>
              );
            })
          ) : (
            <ItemS noItems>No items</ItemS>
          )}
        </ItemsWrapperS>
      )}
    </WrapperS>
  );
};

const WrapperS = styled.div`
  position: relative;
  width: 100%;
`;

const SelectBtnS = styled.div<{ isOpen?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  border: solid 1px ${RGBA_COLORS.getDefault(0.2)};
  border-radius: 0;
  height: 35px;
  align-items: center;
  padding: 13px 16px;
  cursor: pointer;
  .svgIconSelect {
    margin-right: 10px;
  }

  &:hover {
    border-color: ${RGBA_COLORS.getAccent(1)};
  }

  svg {
    transition: 0.2s;
    ${({ isOpen }) =>
      isOpen
        ? `
   transform: translateY(1px) scale(1, -1);
  `
        : ''}
  }
`;

const SelectSpanS = styled.span<{ active?: boolean }>`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  padding-right: 10px;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ active }) => (active ? `  opacity: 1;` : `  opacity: 0.6;`)}
`;

const LabelS = styled.div`
  span {
    font-size: 0.8rem;
    display: block;
    color: ${RGBA_COLORS.getDefault(0.8)};
    font-weight: 400;
  }
  margin-bottom: 5px;
`;

const ItemsWrapperS = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 300px;
  overflow: auto;
  border-radius: 10px;
  box-shadow: 0 4px 23px 0 rgba(42, 47, 42, 0.16);
  background-color: #ffffff;
  margin: 0;
  padding: 0;
  z-index: 2;
`;
const ItemS = styled.li<{ noItems?: boolean }>`
  span {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    padding: 13px 16px;
    letter-spacing: normal;
    display: block;
    cursor: pointer;

    &:hover {
      background: ${RGBA_COLORS.getAccent(0.13)};
    }

    &:active {
      background: ${RGBA_COLORS.getAccent(1)};
      color: white;
    }
  }

  ${({ noItems }) =>
    noItems
      ? `
    padding: 10px;
  `
      : ``}
`;
